import React from "react";
import { Dialog, DialogContent, DialogTitle } from "~/components/dialog";
import { useSnackBar } from "~/components/snackbar";
import TextFormField from "~/components/text-form-field";
import FormBottomRow from "~/components/form-bottom-row";
import Button from "~/components/button";
import { useEditProjectGroupMutation } from "./mutation.generated";
import { useReload } from "../CustomerContext";
import { FieldOffice, ValidationResponse } from "~/gql/types";
import { ErrorRenderer, MutationForm } from "~/components/forms/MutationForm";
import FieldOfficePickerFormField from "~/visuals/organisms/FieldOfficePickerFormField";

type EditProjectGroupProps = {
  open: boolean;
  setOpen: React.Dispatch<boolean>;
  customerNumber: string;
  projectGroupName: string;
  billingOffice: FieldOffice | null;
};

type EditProjectGroupFormValues = {
  name: string | null;
  billingOffice: FieldOffice | null;
};

type EditProjectGroupFormProps = Omit<EditProjectGroupProps, "open">;

export const EditProjectGroup: React.FC<EditProjectGroupProps> = (props) => {
  const { open, setOpen } = props;
  return (
    <>
      <Button icon="edit" onClick={() => setOpen(true)} />
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        className="edit-project-group"
      >
        <DialogTitle>Edit Project Group</DialogTitle>
        <DialogContent>
          {open && <EditProjectGroupForm {...props} />}
        </DialogContent>
      </Dialog>
    </>
  );
};

export const EditProjectGroupForm: React.FC<EditProjectGroupFormProps> = ({
  setOpen,
  customerNumber,
  projectGroupName,
  billingOffice,
}) => {
  const [doMutation] = useEditProjectGroupMutation();

  const addAlert = useSnackBar();
  const reload = useReload();

  const onSuccess = () => {
    const message = "Project Group edited";
    addAlert({
      isSuccess: true,
      message,
      key: `${Math.random()}`,
    });
    setOpen(false);
    reload();
  };
  const handleSubmit = async (values: EditProjectGroupFormValues) => {
    const variables = {
      customerNumber: customerNumber,
      oldName: projectGroupName,
      newName: values.name!,
      billingOfficeCode: values.billingOffice?.officeCode ?? null,
    };
    const result = await doMutation({ variables });
    return result?.data?.projectGroups?.edit as ValidationResponse;
  };

  const initialValues: EditProjectGroupFormValues = {
    name: projectGroupName,
    billingOffice: billingOffice,
  };
  return (
    <MutationForm
      initialValues={initialValues}
      onSuccess={onSuccess}
      runMutation={handleSubmit}
    >
      <TextFormField
        formField={"name"}
        label={"Name"}
        helperText={""}
        required
      />
      <FieldOfficePickerFormField
        formField={"billingOffice"}
        label={"Billing Office"}
        helperText={"Optional. Determines payee info and logo on CRI PDFs."}
      />
      <ErrorRenderer
        render={(formError) => (
          <FormBottomRow
            errorMessage={formError}
            buttonText="Save"
            onCancel={() => setOpen(false)}
          />
        )}
      />
    </MutationForm>
  );
};
